import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, API, bg, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mic, MicOff, BookmarkPlus, BookmarkCheck, FileText, Trash2, ArrowLeft, Send, Loader2, X, ThumbsUp, ThumbsDown, MessageSquarePlus, GraduationCap, Lock, ShoppingBag, MessageCircle, Smartphone } from "lucide-react";
import { Texto, FichaTecnica } from "@/components/IATexto";
import { useAuth } from "@/context/AuthContext";

const VERDE = "#1E9E2A";
export const NOME_IA = "IEP Intelligence";
// a assistente se apresenta como a versão virtual da professora (proposta da Keyla, 03/11): marca = IEP INTELLIGENCE, assistente = Keyla
export const ASSISTENTE = "Keyla Sampaio Virtual";

/** Núcleo animado da IEP INTELLIGENCE (anéis girando; mais rápido quando está pensando). */
export function Nucleo({ tamanho = 28, pensando = false, claro = false, cor = false }) {
  return <span className={`nucleo ${pensando ? "pensando" : ""} ${claro ? "claro" : ""} ${cor ? "cor" : ""}`} style={{ width: tamanho, height: tamanho }} aria-hidden="true"><i /><i /><b /></span>;
}
/** O núcleo da IEP INTELLIGENCE no lugar de um ícone (menus, abas, títulos): segue a cor do texto em volta. */
export function NucleoIcone({ className = "" }) {
  const t = /h-(\d+)/.exec(className); const px = t ? Number(t[1]) * 4 + 2 : 20;
  return <Nucleo tamanho={px} cor />;
}
const semCodigo = (d) => String(formatApiError(d) || "").replace(/^[A-Z_]+:\s*/, "");
const reais = (v) => Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const AVISO = "iep-ia-novidade-vista";
const lerAviso = () => { try { return localStorage.getItem(AVISO) === "1"; } catch { return false; } };
const marcarAviso = () => { try { localStorage.setItem(AVISO, "1"); } catch { /* sem armazenamento: o aviso volta na próxima visita */ } };

/** Estado da IA para a aluna (saldo, pausa da prova, histórico). null = carregando ou ainda não liberada. */
export function useStatusIA() {
  const { user } = useAuth();
  const conta = user?.id ? `${user.id}-${user.role}` : "";
  const [st, setSt] = useState(null);
  const carregar = useCallback(() => api.get("/innova-ia/status", bg).then((r) => setSt(r.data)).catch(() => setSt(null)), []);
  // trocou de conta (saiu e entrou com outra): começa do zero, sem nada da conta anterior
  useEffect(() => { setSt(null); if (conta) carregar(); }, [conta, carregar]);
  return [st, setSt, carregar];
}

/** Uma pergunta e a resposta, como num chat: só o texto (a própria resposta diz a aula e o minuto para estudar),
 *  com 👍/👎 e "Perguntar à professora" discretos embaixo. */
function Resposta({ r, onMudou, aulaId, onAssistir, prof }) {
  const nota = async (n) => { try { await api.post(`/innova-ia/${r.id}/nota`, { nota: n }); onMudou({ ...r, nota_aluna: n }); } catch (e) { toast.error(semCodigo(e.response?.data?.detail)); } };
  const professora = async () => {
    try {
      const x = await api.post(`/innova-ia/${r.id}/professora`);
      toast.success(x.data.turma ? `Dúvida enviada para a professora da turma ${x.data.turma}.` : "Dúvida enviada para a equipe do IEP.");
      onMudou({ ...r, duvida: { status: "aberta" } });
    } catch (e) { toast.error(semCodigo(e.response?.data?.detail)); }
  };
  const texto = (r.resposta || "").replace(/\s?\[\d+\]/g, "");
  return (
    <div className="space-y-2">
      <div className="ml-auto max-w-[85%] w-fit rounded-2xl rounded-br-sm bg-[#232D4F] text-white px-3.5 py-2 text-sm">{r.pergunta}</div>
      <div className="flex items-start gap-2">
        <span className="h-7 w-7 shrink-0 rounded-full bg-[#1A2340] grid place-items-center"><Nucleo tamanho={22} pensando={r.carregando} /></span>
        <div className="flex-1 min-w-0">
          <div className="rounded-2xl rounded-tl-sm bg-white border border-slate-200 px-3.5 py-2.5">
            {r.carregando && !r.resposta
              ? <div className="flex items-center gap-2 text-sm text-slate-500">Pensando…</div>
              : <Texto t={texto} revisada={r.revisada} acoesFicha={r.id && !r.carregando ? <SalvarFicha rid={r.id} /> : null} />}
            {r.duvida?.status === "aberta" && <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-[12px] text-amber-800">Sua dúvida foi enviada para a professora. A resposta aparece aqui. ❤️</div>}
            {r.duvida?.status === "respondida" && <div className="mt-2 rounded-lg bg-green-50 border border-green-200 px-2.5 py-2 text-[13px] text-slate-700"><b className="text-[#1E9E2A]">A professora respondeu:</b> {r.duvida.resposta}</div>}
          </div>
          {!r.trava_prova && !r.carregando && (
            <div className="mt-1 flex items-center gap-1 pl-1">
              <button onClick={() => nota("bom")} className={`rounded-full p-1 ${r.nota_aluna === "bom" ? "text-[#1E9E2A]" : "text-slate-300 hover:text-slate-500"}`} title="Ajudou"><ThumbsUp className="h-3.5 w-3.5" /></button>
              <button onClick={() => nota("ruim")} className={`rounded-full p-1 ${r.nota_aluna === "ruim" ? "text-red-500" : "text-slate-300 hover:text-slate-500"}`} title="Não ajudou"><ThumbsDown className="h-3.5 w-3.5" /></button>
              {!r.duvida && !prof && <button onClick={professora} className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-600"><GraduationCap className="h-3.5 w-3.5" /> Perguntar à professora</button>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Ditado: a pessoa fala a pergunta e o texto aparece no campo (reconhecimento de voz do próprio navegador, em português).
 *  No atendimento, com a mão no cabelo da cliente, é mais fácil falar do que digitar. Some se o navegador não tiver o recurso. */
const Reconhecedor = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
function useDitado(aoMudar) {
  const [ouvindo, setOuvindo] = useState(false);
  const rec = useRef(null);
  const base = useRef("");
  const parar = useCallback(() => { try { rec.current?.stop(); } catch { /* já parou */ } }, []);
  const comecar = useCallback((textoAtual) => {
    if (!Reconhecedor) { toast.info("Este navegador não faz o ditado. Use o Google Chrome ou toque no microfone do teclado do celular.", { duration: 7000 }); return; }
    const r = new Reconhecedor();
    r.lang = "pt-BR"; r.interimResults = true; r.continuous = true;
    base.current = textoAtual ? textoAtual.trim() + " " : "";
    r.onresult = (e) => {
      let t = "";
      for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript;
      aoMudar((base.current + t).replace(/\s+/g, " ").trimStart());
    };
    r.onerror = (e) => { if (e.error === "not-allowed" || e.error === "service-not-allowed") toast.error("Permita o uso do microfone para ditar a pergunta."); };
    r.onend = () => setOuvindo(false);
    rec.current = r;
    try { r.start(); setOuvindo(true); } catch { setOuvindo(false); }
  }, [aoMudar]);
  useEffect(() => () => parar(), [parar]);
  return { disponivel: !!Reconhecedor, ouvindo, comecar, parar };
}

/** Botão "Salvar em Minhas fichas" de uma ficha técnica. */
function SalvarFicha({ rid }) {
  const [salva, setSalva] = useState(false);
  const salvar = async () => {
    try { await api.post(`/innova-ia/${rid}/ficha`); setSalva(true); toast.success("Ficha salva em Minhas fichas."); }
    catch (e) { toast.error(semCodigo(e.response?.data?.detail)); }
  };
  return salva
    ? <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#1E9E2A]"><BookmarkCheck className="h-3.5 w-3.5" /> Salva</span>
    : <button type="button" onClick={salvar} className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#1E9E2A] hover:underline" data-testid="ia-salvar-ficha"><BookmarkPlus className="h-3.5 w-3.5" /> Salvar em Minhas fichas</button>;
}

// cópia das fichas neste aparelho: o app da Keyla abre "Minhas fichas" mesmo sem internet (no salão, com a cliente na cadeira)
const FICHAS_KEY = "iep-ia-fichas";
const guardarFichas = (l) => { try { localStorage.setItem(FICHAS_KEY, JSON.stringify(l || [])); } catch { /* sem armazenamento: só não abre offline */ } };
export const fichasGuardadas = () => { try { return JSON.parse(localStorage.getItem(FICHAS_KEY) || "null"); } catch { return null; } };
export const apagarFichasGuardadas = () => { try { localStorage.removeItem(FICHAS_KEY); } catch { /* nada a apagar */ } };

/** Fichas técnicas que a pessoa salvou: abrir, imprimir e apagar. */
export function MinhasFichas({ voltar }) {
  const [lista, setLista] = useState(null);
  const [aberta, setAberta] = useState(null);
  const carregar = useCallback(() => api.get("/innova-ia/fichas").then((r) => { setLista(r.data.fichas); guardarFichas(r.data.fichas); }).catch(() => setLista(fichasGuardadas() || [])), []);
  useEffect(() => { carregar(); }, [carregar]);
  const apagar = async (f) => {
    try { await api.delete(`/innova-ia/fichas/${f.id}`); setAberta(null); carregar(); toast.success("Ficha apagada."); }
    catch (e) { toast.error(semCodigo(e.response?.data?.detail)); }
  };
  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-[#F8FAFC] px-3 py-3" data-testid="ia-minhas-fichas">
      <button onClick={aberta ? () => setAberta(null) : voltar} className="mb-2 inline-flex items-center gap-1 text-[13px] font-semibold text-slate-600 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> {aberta ? "Minhas fichas" : "Voltar para a conversa"}</button>
      {lista === null ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        : aberta ? <FichaTecnica bloco={aberta.texto} revisada={aberta.revisada}
            acoes={<button onClick={() => apagar(aberta)} className="inline-flex items-center gap-1 text-[12px] font-semibold text-red-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /> Apagar</button>} />
        : lista.length === 0 ? <p className="px-2 py-6 text-center text-sm text-slate-500">Você ainda não salvou nenhuma ficha. Peça uma fórmula à IA e toque em “Salvar em Minhas fichas”.</p>
        : <div className="space-y-2">{lista.map((f) => (
            <button key={f.id} onClick={() => setAberta(f)} className="flex w-full items-start gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left hover:border-[#1E9E2A]">
              <FileText className="mt-0.5 h-4 w-4 shrink-0 text-[#1E9E2A]" />
              <span className="min-w-0 flex-1"><span className="block font-semibold text-slate-800 truncate">{f.titulo}</span>
                <span className="block text-[11px] text-slate-500">{new Date(f.at).toLocaleDateString("pt-BR")}{f.revisada ? " · revisada pela equipe" : ""}</span></span>
            </button>))}</div>}
    </div>
  );
}

/** Conversa com a IA: saldo, mensagens, pacotes quando acabar e pausa da prova.
 *  Na tela da aula (aula = {id, titulo}) mostra só as perguntas feitas nesta aula, a IA procura primeiro nela
 *  e o "▶ assistir" desta aula pula o vídeo (onAssistir). */
export function PainelIA({ st, setSt, carregar, aula, onAssistir, compacto }) {
  const [itens, setItens] = useState([]);
  const [q, setQ] = useState("");
  const ditado = useDitado(setQ);
  const [busy, setBusy] = useState(false);
  const [conversa, setConversa] = useState("");
  const [comprando, setComprando] = useState("");
  const fim = useRef(null);
  // a janela abre limpa (só a saudação); as conversas anteriores aparecem só se a pessoa pedir
  const anteriores = (st?.historico || []).filter((r) => (aula ? r.aula_id === aula.id : true));
  const [verAntigas, setVerAntigas] = useState(false);
  useEffect(() => { setItens([]); setVerAntigas(false); }, [aula?.id]);
  // a janela abre limpa, mas a IA continua a última conversa se ela foi nas últimas 12 h ("vamos continuar" funciona);
  // "Nova conversa" começa do zero
  const CONTINUA_HORAS = 12;
  const ultima = anteriores[anteriores.length - 1];
  const recente = !!ultima?.conversa_id && Date.now() - new Date(ultima.at).getTime() < CONTINUA_HORAS * 3600e3;
  const [continuou, setContinuou] = useState(false);
  useEffect(() => { if (recente && !conversa) { setConversa(ultima.conversa_id); setContinuou(true); } }, [recente]); // eslint-disable-line react-hooks/exhaustive-deps
  const mostrarAntigas = () => { setVerAntigas(true); setItens((xs) => [...anteriores.filter((a) => !xs.some((x) => x.id === a.id)), ...xs]); };
  useEffect(() => { if (!compacto) setTimeout(() => fim.current?.scrollIntoView({ block: "end" }), 50); }, [itens.length, compacto]);

  const saldoTotal = st.gratis_restantes + st.creditos;
  // a resposta vai aparecendo enquanto é escrita (linhas JSON: fontes → pedaços de texto → fim)
  const perguntar = async () => {
    const p = q.trim();
    if (p.length < 3) return;
    setBusy(true);
    const tmp = `tmp-${Date.now()}`;
    const aulaId = aula?.id || "";
    const troca = (f) => setItens((xs) => xs.map((x) => (x.id === tmp ? f(x) : x)));
    setItens((xs) => [...xs, { id: tmp, pergunta: p, resposta: "", fontes: [], aula_id: aulaId, carregando: true }]); setQ("");
    try {
      const res = await fetch(`${API}/innova-ia/perguntar-stream`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pergunta: p, conversa_id: conversa, aula_id: aulaId }) });
      if (!res.ok) { let detail; try { detail = (await res.json()).detail; } catch { /* resposta sem JSON */ } throw { response: { data: { detail } } }; }
      const leitor = res.body.getReader(); const dec = new TextDecoder(); let buf = ""; let fim = false;
      while (!fim) {
        const { value, done } = await leitor.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let k;
        while ((k = buf.indexOf("\n")) >= 0) {
          const linha = buf.slice(0, k); buf = buf.slice(k + 1);
          if (!linha.trim()) continue;
          const e = JSON.parse(linha);
          if (e.fontes) troca((x) => ({ ...x, fontes: e.fontes }));
          else if (e.texto) troca((x) => ({ ...x, resposta: x.resposta + e.texto }));
          else if (e.fim) {
            fim = true; troca(() => ({ ...e.fim, aula_id: aulaId }));
            setConversa(e.fim.conversa_id); setSt((s) => ({ ...s, ...e.fim.saldo })); marcarAviso();
          } else if (e.erro) throw { response: { data: { detail: e.erro } } };
        }
      }
      if (!fim) throw { response: { data: { detail: "A IEP Intelligence não respondeu agora. Tente de novo em instantes." } } };
    } catch (e) {
      setItens((xs) => xs.filter((x) => x.id !== tmp)); setQ(p);
      const d = String(e?.response?.data?.detail || "");
      if (d.startsWith("PROVA_ABERTA") || d.startsWith("SEM_SALDO") || d.startsWith("EXPERIMENTE_FIM")) carregar();
      toast.error(semCodigo(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const comprar = async (id) => {
    setComprando(id);
    try { const r = await api.post("/innova-ia/comprar", { pacote: id }); window.location.assign(r.data.init_point); }
    catch (e) { toast.error(semCodigo(e.response?.data?.detail)); setComprando(""); }
  };
  const trocar = (novo) => setItens((xs) => xs.map((x) => (x.id === novo.id ? novo : x)));

  return (
    <div className={`flex flex-col min-h-0 ${compacto ? "" : "flex-1"}`}>
      <div className="px-4 py-2 text-[12px] bg-white border-b border-slate-200 flex flex-wrap items-center gap-x-3 gap-y-1">
        {st.teste && <span className="font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Teste do admin: não gasta perguntas</span>}
        {st.nivel === "professora" ? <span className="font-semibold text-[#1E9E2A]">Acesso de professora · perguntas ilimitadas</span>
          : st.nivel === "experimente"
          ? <span><b>{st.gratis_restantes}</b> de {st.gratis_total} {st.gratis_total === 1 ? "pergunta" : "perguntas"} para experimentar</span>
          : st.gratis_total > 0 ? <span><b>{st.gratis_restantes}</b> de {st.gratis_total} grátis {st.periodo === "mes" ? "este mês" : ""}</span> : null}
        {st.creditos > 0 && <span><b>{st.creditos}</b> {st.creditos === 1 ? "pergunta comprada" : "perguntas compradas"}</span>}
        {conversa && <button onClick={() => { setConversa(""); setContinuou(false); setItens([]); setVerAntigas(false); }} className="ml-auto inline-flex items-center gap-1 font-semibold text-slate-500 hover:text-slate-800"><MessageSquarePlus className="h-3.5 w-3.5" /> Nova conversa</button>}
      </div>
      <div className={`overflow-y-auto px-3 py-4 space-y-5 bg-[#F8FAFC] ${compacto ? "max-h-[520px]" : "flex-1"}`}>
        {!verAntigas && anteriores.length > 0 && (
          <div className="text-center"><button onClick={mostrarAntigas} className="text-[12px] font-semibold text-slate-400 hover:text-slate-600">{continuou && itens.length === 0 ? "Continuando a sua última conversa · ver" : `Ver conversas anteriores (${anteriores.length})`}</button></div>
        )}
        {itens.length === 0 && (
          <div className="text-center text-sm text-slate-500 px-4 pt-2">
            <span className="h-14 w-14 mx-auto rounded-full bg-[#1A2340] grid place-items-center shadow-lg"><Nucleo tamanho={44} /></span>
            {aula ? <><p className="mt-3 font-semibold text-slate-700">Ficou com dúvida nesta aula? ❤️</p>
              <p className="mt-1">Pergunte sobre “{aula.titulo}”. Eu procuro primeiro nesta aula e te mostro o minuto para rever.</p></>
              : <><p className="mt-3 font-semibold text-slate-700">Olá! Eu sou a {ASSISTENTE}, a inteligência científica do nosso Método. ❤️</p>
              <p className="mt-1">Pode me perguntar sobre fórmulas, procedimentos e cuidados com o cabelo da sua cliente: eu respondo com base nas aulas e mostro onde rever.</p></>}
          </div>
        )}
        {itens.map((r) => <Resposta key={r.id} r={r} onMudou={trocar} aulaId={aula?.id} onAssistir={onAssistir} prof={st.nivel === "professora"} />)}
        <div ref={fim} />
      </div>
      <div className="border-t border-slate-200 bg-white p-3">
        {st.pausa_prova ? (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            <Lock className="h-4 w-4 mt-0.5 shrink-0" /> Você está com uma prova aberta. A {ASSISTENTE} volta assim que você terminar a prova. Boa prova! ❤️
          </div>
        ) : saldoTotal <= 0 && !st.teste && st.nivel !== "professora" && st.nivel === "experimente" ? (
          <div className="space-y-2 text-center">
            <p className="text-sm text-slate-700"><b>Gostou da {NOME_IA}? ❤️</b><br />Para continuar usando, fale com a nossa equipe.</p>
            <a href={`https://wa.me/${st.whatsapp}?text=${encodeURIComponent(`Olá! Experimentei a ${NOME_IA} e quero continuar usando.`)}`} target="_blank" rel="noopener"
              className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-bold text-white w-full" style={{ background: "#25D366" }}>
              <MessageCircle className="h-4 w-4" /> Falar no WhatsApp
            </a>
          </div>
        ) : saldoTotal <= 0 && !st.teste && st.nivel !== "professora" ? (
          <div className="space-y-2">
            <p className="text-sm text-slate-600"><ShoppingBag className="inline h-4 w-4 -mt-0.5 mr-1 text-[#1E9E2A]" />{st.gratis_total > 0 ? `Suas perguntas ${st.periodo === "mes" ? "deste mês " : ""}acabaram. Escolha um pacote para continuar:` : "Escolha um pacote de perguntas para começar:"}</p>
            <div className="grid grid-cols-3 gap-2">
              {(st.pacotes || []).map((p) => (
                <button key={p.id} disabled={!!comprando} onClick={() => comprar(p.id)} className="rounded-xl border border-slate-200 p-2 text-center hover:border-[#1E9E2A] hover:bg-green-50 disabled:opacity-60">
                  <div className="font-display text-lg font-bold text-slate-800">{p.qtd}</div>
                  <div className="text-[11px] text-slate-500">perguntas</div>
                  <div className="mt-1 text-sm font-bold text-[#1E9E2A]">{comprando === p.id ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : reais(p.preco)}</div>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-400">Pagamento por Pix ou cartão no Mercado Pago. As perguntas entram assim que o pagamento é aprovado e não vencem.</p>
          </div>
        ) : (
          <div className="flex items-end gap-2">
            <Textarea value={q} onChange={(e) => setQ(e.target.value)} rows={2} maxLength={1000} placeholder={ditado.ouvindo ? "Pode falar… toque no microfone para parar" : conversa ? "Continue a conversa com a Keyla…" : aula ? "Pergunte à Keyla sobre esta aula…" : ditado.disponivel ? "Pergunte à Keyla (ou toque no 🎤 e fale)…" : "Pergunte à Keyla…"}
              className={`resize-none ${ditado.ouvindo ? "border-red-300 ring-2 ring-red-200" : ""}`}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); perguntar(); } }} data-testid="innova-ia-pergunta" />
            <button type="button" onClick={() => (ditado.ouvindo ? ditado.parar() : ditado.comecar(q))} disabled={busy}
              className={`h-10 w-10 shrink-0 rounded-md grid place-items-center border ${ditado.ouvindo ? "bg-red-500 border-red-500 text-white animate-pulse" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
              aria-label={ditado.ouvindo ? "Parar de ouvir" : "Falar a pergunta"} title={ditado.ouvindo ? "Parar" : "Falar a pergunta"} data-testid="innova-ia-microfone">
              {ditado.ouvindo ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </button>
            <Button onClick={() => { ditado.parar(); perguntar(); }} disabled={busy || q.trim().length < 3} className="text-white h-10 w-10 p-0 shrink-0" style={{ background: VERDE }} aria-label="Enviar">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Botão flutuante + janela da IEP INTELLIGENCE (antiga In’Nova IA) na área da aluna. Não aparece enquanto o admin não liberar para as alunas.
 *  Na tela da aula o botão some no celular (lá a dúvida fica na aba "Dúvida sobre esta aula"); na prova some sempre. */
export function InnovaIAAluna() {
  const [st, setSt, carregar] = useStatusIA();
  const [aberta, setAberta] = useState(false);
  const [fichas, setFichas] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { if (aberta) carregar(); }, [aberta, carregar]);
  useEffect(() => { const f = () => setAberta(true); window.addEventListener("abrir-innova-ia", f); return () => window.removeEventListener("abrir-innova-ia", f); }, []);
  if (!st || /\/prova$/.test(pathname)) return null;
  const naAula = /^\/(app|prof)\/curso\//.test(pathname);
  return (
    <>
      {!aberta && (
        <button onClick={() => setAberta(true)} data-testid="innova-ia-botao"
          className={`fixed bottom-5 right-5 z-40 items-center gap-2 rounded-full px-4 py-3 text-sm font-bold text-white shadow-xl hover:scale-105 transition-transform ${naAula ? "hidden sm:inline-flex" : "inline-flex"}`}
          style={{ background: "linear-gradient(135deg,#232D4F,#1E9E2A)" }}>
          <Nucleo tamanho={24} /> Pergunte à Keyla
        </button>
      )}
      {aberta && (
        <div className={`fixed z-50 sm:right-5 sm:w-[420px] flex flex-col ${st.teste   /* "ver como aluna": a faixa laranja do admin fica embaixo, a janela sobe */
          ? "inset-x-0 top-0 bottom-24 sm:inset-x-auto sm:top-auto sm:bottom-24 sm:h-[min(640px,calc(100vh-120px))]"
          : "inset-0 sm:inset-auto sm:bottom-5 sm:h-[min(680px,calc(100vh-40px))]"} bg-[#F8FAFC] sm:rounded-2xl shadow-2xl border border-slate-200 overflow-hidden`} data-testid="innova-ia-janela">
          <div className="px-4 py-3 text-white flex items-center gap-2" style={{ background: "linear-gradient(135deg,#1A2340,#232D4F 60%,#1E9E2A)" }}>
            <Nucleo tamanho={34} />
            <div className="min-w-0">
              <div className="font-display font-bold leading-tight tracking-wide">IEP <span className="text-[#A7F3B0]">INTELLIGENCE</span></div>
              <div className="text-[11px] text-slate-200 truncate">{ASSISTENTE} · Inteligência Científica Capilar</div>
            </div>
            <a href="/keyla" target="_blank" rel="noopener" title="Abrir como app no celular" className="ml-auto inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[12px] font-semibold hover:bg-white/20" data-testid="ia-abrir-app"><Smartphone className="h-3.5 w-3.5" /> App</a>
            <button onClick={() => setFichas(!fichas)} className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[12px] font-semibold hover:bg-white/20" data-testid="ia-botao-fichas"><FileText className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Minhas&nbsp;</span>fichas</button>
            <button onClick={() => setAberta(false)} className="rounded-full p-1.5 hover:bg-white/15" aria-label="Fechar"><X className="h-5 w-5" /></button>
          </div>
          {fichas ? <MinhasFichas voltar={() => setFichas(false)} /> : <PainelIA st={st} setSt={setSt} carregar={carregar} />}
        </div>
      )}
    </>
  );
}

/** Aba "Dúvida sobre esta aula" (abaixo do vídeo). Some quando a IA não está liberada ou para quem não é aluna. */
export function useIADisponivel() {
  const [st] = useStatusIA();
  return !!st;
}

export function DuvidaAulaIA({ aula, onAssistir }) {
  const [st, setSt, carregar] = useStatusIA();
  if (!st) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  return (
    <div className="-m-5 rounded-b-xl overflow-hidden" data-testid="innova-ia-aula">
      <PainelIA st={st} setSt={setSt} carregar={carregar} aula={aula} onAssistir={onAssistir} compacto />
    </div>
  );
}

/** Cartão de novidade no topo da lista de cursos (some depois que a aluna usa a IA ou fecha o aviso). */
export function NovidadeIA() {
  const [st] = useStatusIA();
  const [visto, setVisto] = useState(lerAviso);
  if (!st || visto || (st.historico || []).length) return null;
  const fechar = () => { marcarAviso(); setVisto(true); };
  return (
    <div className="max-w-6xl mx-auto w-full px-5 md:px-8 pt-5">
      <div className="relative rounded-2xl p-5 text-white flex flex-wrap items-center gap-4 shadow-lg" style={{ background: "linear-gradient(135deg,#1A2340,#232D4F 55%,#1E9E2A)" }} data-testid="innova-ia-novidade">
        <span className="h-12 w-12 rounded-full bg-white/10 grid place-items-center shrink-0"><Nucleo tamanho={40} /></span>
        <div className="flex-1 min-w-[220px]">
          <div className="text-[11px] font-bold uppercase tracking-wider text-green-200">Novidade</div>
          <div className="font-display text-lg font-bold">Conheça a {ASSISTENTE}</div>
          <p className="text-sm text-slate-200">Tire dúvidas sobre as aulas e os materiais do Método e veja o minuto exato para rever. {st.nivel === "experimente" ? `Experimente grátis: ${st.gratis_total} ${st.gratis_total === 1 ? "pergunta" : "perguntas"}.` : st.gratis_total > 0 ? `${st.gratis_total} perguntas grátis ${st.periodo === "mes" ? "por mês" : ""}.` : "A IEP INTELLIGENCE: tire dúvidas de fórmulas e procedimentos, com a aula e o minuto para rever."}</p>
        </div>
        <Button onClick={() => { fechar(); window.dispatchEvent(new Event("abrir-innova-ia")); }} className="bg-white text-[#232D4F] hover:bg-slate-100 font-bold">Experimentar</Button>
        <button onClick={fechar} className="absolute top-2 right-2 rounded-full p-1 text-white/70 hover:bg-white/15" aria-label="Fechar aviso"><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
}
